import React, { useRef, useState } from "react";
import {
  Bold, Italic, Link as LinkIcon, Image as ImageIcon, Sticker,
  Quote as QuoteIcon, List as ListIcon, Code as CodeIcon, X,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { STICKERS, GIFS } from "@/data/stickers";
import { t } from "@/lib/i18n";

// A lightweight WYSIWYG built on contenteditable — supports bold/italic/link/image URL/
// blockquote/list/code, plus a sticker+gif picker and a "quote reply" chip.
export default function WysiwygEditor({
  value,
  onChange,
  placeholder,
  quoted,
  onRemoveQuote,
  minHeight = 200,
  testid = "wysiwyg-editor",
}) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  // Set initial content once
  React.useEffect(() => {
    if (!ref.current) return;
    if (!ready) {
      ref.current.innerHTML = value || "";
      setReady(true);
    }
  }, [value, ready]);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (cmd, val = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    emit();
  };

  const insertHTML = (html) => {
    ref.current?.focus();
    document.execCommand("insertHTML", false, html);
    emit();
  };

  const onInsertLink = () => {
    const url = window.prompt(t.linkUrlPrompt);
    if (!url) return;
    const text = window.prompt(t.linkTextPrompt, url) || url;
    insertHTML(`<a href="${url}" target="_blank" rel="noopener">${text}</a>`);
  };

  const onInsertImage = () => {
    const url = window.prompt(t.imageUrlPrompt);
    if (!url) return;
    insertHTML(`<img src="${url}" alt="" />`);
  };

  const insertSticker = (url) => insertHTML(`<img src="${url}" class="sticker-img" alt="sticker" />`);
  const insertGif = (url) => insertHTML(`<img src="${url}" alt="gif" style="max-height:180px;border-radius:8px;" />`);

  const toolBtn = (icon, onClick, title, tid) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="p-2 rounded-md text-foreground/70 hover:bg-primary/10 hover:text-primary transition-colors"
      data-testid={tid}
    >
      {icon}
    </button>
  );

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid={testid}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-border bg-muted/60 font-eng text-sm" dir="ltr">
        {toolBtn(<Bold size={16} />, () => exec("bold"), t.bold, "editor-bold")}
        {toolBtn(<Italic size={16} />, () => exec("italic"), t.italic, "editor-italic")}
        {toolBtn(<LinkIcon size={16} />, onInsertLink, t.linkText, "editor-link")}
        {toolBtn(<ImageIcon size={16} />, onInsertImage, t.image, "editor-image")}
        {toolBtn(<QuoteIcon size={16} />, () => exec("formatBlock", "blockquote"), t.quote, "editor-quote")}
        {toolBtn(<ListIcon size={16} />, () => exec("insertUnorderedList"), t.list, "editor-list")}
        {toolBtn(<CodeIcon size={16} />, () => insertHTML("<code>&nbsp;</code>"), t.code, "editor-code")}

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="p-2 rounded-md text-foreground/70 hover:bg-primary/10 hover:text-primary transition-colors flex items-center gap-1"
              title={t.gif}
              data-testid="editor-sticker-trigger"
            >
              <Sticker size={16} />
              <span className="text-xs font-medium">GIF</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3" align="start">
            <div className="text-xs font-eng font-semibold mb-2 text-primary">Stickers</div>
            <div className="grid grid-cols-6 gap-2 mb-3">
              {STICKERS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => insertSticker(s.url)}
                  className="p-1 rounded hover:bg-primary/10 transition-colors"
                  title={s.label}
                  data-testid={`sticker-${s.id}`}
                >
                  <img src={s.url} alt={s.label} className="w-9 h-9 object-contain" />
                </button>
              ))}
            </div>
            <div className="text-xs font-eng font-semibold mb-2 text-primary">GIFs</div>
            <div className="grid grid-cols-3 gap-2">
              {GIFS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => insertGif(g.url)}
                  className="rounded overflow-hidden border border-border hover:border-primary transition-colors"
                  title={g.label}
                  data-testid={`gif-${g.id}`}
                >
                  <img src={g.url} alt={g.label} className="w-full h-16 object-cover" />
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Quoted reply chip */}
      {quoted && (
        <div className="flex items-start gap-2 px-4 pt-3">
          <div className="flex-1 border-r-2 border-primary bg-muted/70 px-3 py-2 rounded-md text-sm">
            <div className="text-xs font-eng font-semibold text-primary mb-1">
              {t.quote}: {quoted.username}
            </div>
            <div
              className="line-clamp-3 opacity-80"
              dangerouslySetInnerHTML={{ __html: quoted.content }}
            />
          </div>
          <Button size="icon" variant="ghost" onClick={onRemoveQuote} title={t.removeQuote} data-testid="editor-remove-quote">
            <X size={16} />
          </Button>
        </div>
      )}

      {/* Editable body */}
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder || t.yourPost}
        className="wysiwyg-body"
        style={{ minHeight }}
        dir="rtl"
        data-testid="wysiwyg-body"
        suppressContentEditableWarning
      />
    </div>
  );
}
